/**
 * tokenStore.js — Sichere, plattformübergreifende Token-Abstraktion
 *
 * Native iOS : @capacitor/preferences → iOS UserDefaults (hardware-verschlüsselt,
 *              app-sandboxed, deutlich sicherer als WebView-localStorage).
 * Web        : localStorage (rememberMe=true) / sessionStorage (rememberMe=false) —
 *              Verhalten identisch zum bisherigen Stand.
 *
 * ─── Wichtig: init() VOR app.mount() aufrufen (in main.js) ───────────────────
 * init() liest den Token async aus dem Storage und befüllt den In-Memory-Cache.
 * Danach liefert getSync() sofort den gecachten Wert — das erlaubt synchronen
 * Zugriff in Vue Router-Guards und dem Axios-Request-Interceptor, ohne die
 * async-Keychain-Latenz bei jedem Request zu bezahlen.
 *
 * ─── rememberMe-Semantik ─────────────────────────────────────────────────────
 * Web    : true  → localStorage  (Tab schließen behält Token)
 *          false → sessionStorage (Tab schließen löscht Token)
 * Native : Parameter wird ignoriert — Preferences/Keychain ist immer persistent
 *          und trotzdem sicher (kein Unterschied nötig).
 *
 * ─── set() ist async und awaitable ──────────────────────────────────────────
 * Auf echtem Gerät: Preferences.set() dauert <100ms.
 * Simulator-Bug (iOS hängt): withTimeout(1500ms) verhindert ewiges Warten.
 * _cachedToken wird SOFORT synchron gesetzt — await im Aufrufer ist nur für
 * die UserDefaults-Persistenz, nicht für den In-Memory-Zugriff nötig.
 */

import { isNative } from '@/services/platform';

const PREF_KEY = 'authToken';

// ─── In-Memory-Cache ──────────────────────────────────────────────────────────
// Befüllt von init(). Danach liefert getSync() sofort (kein async-Overhead).
let _cachedToken = null;
let _initialized = false;

// ─── Bridge-Timeout-Hilfsfunktion ─────────────────────────────────────────────
// Im iOS-Simulator kann die WKWebView-Netzwerkprozess-Initialisierung fehlschlagen
// ("Failed to resolve host network app id to config"). Das lässt Capacitor-Bridge-Calls
// hängen — sie resolven weder noch rejecten sie.
// withTimeout() racet den Bridge-Call gegen einen Timer. Bei Timeout: Timer gewinnt.
function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise(resolve => setTimeout(resolve, ms)),
  ]);
}

// ─── Lazy-Import von @capacitor/preferences ───────────────────────────────────
// Verhindert Fehler wenn das Plugin im Browser-Build nicht verfügbar ist.
let _prefs = null;
async function getPreferences() {
  if (_prefs) return _prefs;
  const { Preferences } = await import('@capacitor/preferences');
  _prefs = Preferences;
  return _prefs;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export const tokenStore = {
  /**
   * MUSS vor app.mount() aufgerufen werden.
   * Liest Token aus Storage → befüllt In-Memory-Cache.
   * Idempotent: zweimaliger Aufruf hat keinen Effekt.
   *
   * Timeout (2s): Falls die Capacitor-Bridge hängt (Simulator-Bug), mountet die
   * App trotzdem — schlimmstenfalls als nicht-eingeloggter User auf /login.
   */
  async init() {
    if (_initialized) return;
    _cachedToken = await withTimeout(this.get(), 2000);
    _initialized = true;
  },

  /**
   * Token speichern — async, awaitable.
   *
   * _cachedToken wird SOFORT synchron gesetzt, damit Router-Guards und
   * Axios-Interceptor den Token bereits während des awaits sehen.
   *
   * Danach wird UserDefaults mit Timeout persistiert (max. 1500ms Wartezeit).
   * Auf einem echten Gerät dauert der UserDefaults-Write <50ms.
   * Bei Simulator-Bridge-Hänger: nach 1500ms wird trotzdem weitergemacht
   * (Token ist im Memory-Cache; nächste Session kann ihn nicht lesen, aber
   * die aktuelle Session funktioniert).
   *
   * @param {string}  token   - JWT-Token
   * @param {boolean} persist - nur Web: true→localStorage, false→sessionStorage
   *                            Native ignoriert diesen Parameter.
   */
  async set(token, persist = true) {
    _cachedToken = token; // synchron — sofort für getSync() verfügbar

    if (isNative) {
      try {
        const Prefs = await withTimeout(getPreferences(), 1500);
        if (Prefs) {
          await withTimeout(
            Prefs.set({ key: PREF_KEY, value: token }),
            1500,
          );
        }
      } catch (err) {
        console.warn('[tokenStore] set failed (native):', err);
      }
    } else {
      localStorage.removeItem(PREF_KEY);
      sessionStorage.removeItem(PREF_KEY);
      if (persist) {
        localStorage.setItem(PREF_KEY, token);
      } else {
        sessionStorage.setItem(PREF_KEY, token);
      }
    }
  },

  /**
   * Token lesen — async.
   * Für den initialen Keychain-Read in init().
   * Danach besser getSync() nutzen.
   *
   * Migration (iOS-Umbau): Vor Phase 3 wurde der Token unter dem Key 'jwt' in localStorage
   * gespeichert. Nach dem Umbau heißt der Key 'authToken'. Falls 'authToken' nicht vorhanden,
   * prüfen wir als Fallback den alten 'jwt'-Key und migrieren ihn automatisch.
   *
   * @returns {string|null}
   */
  async get() {
    if (isNative) {
      const Prefs = await getPreferences();
      const { value } = await Prefs.get({ key: PREF_KEY });
      return value || null;
    }

    // Neuer Key (authToken) hat Vorrang — localStorage, dann sessionStorage
    const current = localStorage.getItem(PREF_KEY) || sessionStorage.getItem(PREF_KEY);
    if (current) return current;

    // Migrations-Fallback: alter Key 'jwt' aus der Zeit vor dem iOS-Umbau
    const legacy = localStorage.getItem('jwt');
    if (legacy) {
      // Migrieren: unter neuem Key in localStorage speichern, alten Key entfernen
      try {
        localStorage.setItem(PREF_KEY, legacy);
        localStorage.removeItem('jwt');
      } catch (_) { /* ignore storage errors */ }
      return legacy;
    }

    return null;
  },

  /**
   * Token lesen — synchron (aus In-Memory-Cache).
   * Nur nach init() zuverlässig! Für Router-Guards + Axios-Interceptor.
   * @returns {string|null}
   */
  getSync() {
    return _cachedToken;
  },

  /**
   * Token löschen (Logout / 401 / Passwort-Reset).
   *
   * _cachedToken = null sofort synchron — Axios-Interceptor und Router-Guards
   * sehen den Token sofort als ungültig.
   * Storage-Bereinigung mit Timeout (Simulator-Schutz).
   */
  async clear() {
    _cachedToken = null; // synchron

    if (isNative) {
      try {
        const Prefs = await withTimeout(getPreferences(), 1500);
        if (Prefs) {
          await withTimeout(Prefs.remove({ key: PREF_KEY }), 1500);
        }
      } catch (err) {
        console.warn('[tokenStore] clear failed (native):', err);
      }
    } else {
      localStorage.removeItem(PREF_KEY);
      sessionStorage.removeItem(PREF_KEY);
    }
  },
};
